import { useCallback, useState } from 'react';

interface CursorPagingSource {
    /** Number of pages already loaded by the infinite query. */
    loadedPageCount: number;
    hasNextPage: boolean;
    fetchNextPage: () => Promise<unknown>;
}

/**
 * Page-at-a-time navigation over a cursor-based infinite query. Cursors only
 * move forward, so a page beyond the loaded ones is reachable only as the
 * immediate next page; earlier pages are served from the query cache.
 */
const useCursorPaging = ({ loadedPageCount, hasNextPage, fetchNextPage }: CursorPagingSource) => {
    const [currentPage, setCurrentPage] = useState(1);

    const goToPage = useCallback(async (page: number) => {
        if (page < 1) {
            return;
        }
        if (page <= loadedPageCount) {
            setCurrentPage(page);
            return;
        }
        if (page === loadedPageCount + 1 && hasNextPage) {
            await fetchNextPage();
            setCurrentPage(page);
        }
    }, [loadedPageCount, hasNextPage, fetchNextPage]);

    const resetPaging = useCallback(() => setCurrentPage(1), []);

    return { currentPage, goToPage, resetPaging };
};

export { useCursorPaging };
